import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { Accept, FileRejection } from 'react-dropzone';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { snackbarService } from 'shared/lib/snackbar/service';

const DEFAULT_MAX_FILE_SIZE = 3_000_000;

const StyDropzoneBox = styled(Box, {
    name: 'StyDropzoneBox',
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isInvalid',
})<{ isActive: boolean; isInvalid: boolean }>(
    ({ theme, isActive, isInvalid }) => ({
        position: 'relative',
        width: '100%',
        minHeight: '250px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.paper,
        border: '2px dashed',
        borderColor: isInvalid
            ? theme.palette.error.main
            : isActive
              ? theme.palette.primary.main
              : theme.palette.divider,
        borderRadius: theme.shape.borderRadius,
        boxSizing: 'border-box',
        cursor: 'pointer',
        outline: 'none',
        textAlign: 'center',
    }),
);

const StyPreviewImageContainer = styled(Box, {
    name: 'StyPreviewImageContainer',
})({
    position: 'relative',
    '&:hover button': {
        opacity: 1,
    },
});

const StyPreviewImage = styled('img', {
    name: 'StyPreviewImage',
})({
    maxHeight: 200,
    maxWidth: '100%',
    borderRadius: 4,
    boxShadow: 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
});

const StyRemoveButton = styled(Fab, {
    name: 'StyRemoveButton',
})({
    position: 'absolute',
    top: -16,
    right: -16,
    width: 40,
    height: 40,
    opacity: 0,
    transition: 'opacity .3s ease',
});

type DropzoneZoneProps = {
    accept?: Accept;
    maxFileSize?: number;
    dropzoneText: string;
    previewSrc?: string | null;
    onDropAccepted: (file: File) => void;
    onRemove?: () => void;
};

const DropzoneZone = ({
    accept = undefined,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    dropzoneText,
    previewSrc = null,
    onDropAccepted,
    onRemove = undefined,
}: DropzoneZoneProps) => {
    const { t } = useTranslation();

    const handleDropRejected = (fileRejections: FileRejection[]) => {
        const [rejection] = fileRejections;
        if (!rejection) return;

        const isTooManyFiles = rejection.errors.some(
            (error) => error.code === ErrorCode.TooManyFiles,
        );
        if (isTooManyFiles) {
            snackbarService.showError(t('common.dropzone.tooManyFiles'));
            return;
        }

        const isTooLarge = rejection.errors.some(
            (error) => error.code === ErrorCode.FileTooLarge,
        );
        if (isTooLarge) {
            snackbarService.showError(
                t('common.dropzone.fileTooBig', {
                    fileName: rejection.file.name,
                    fileSize: (rejection.file.size / 1_000_000).toFixed(1),
                    maxSize: maxFileSize
                        ? (maxFileSize / 1_000_000).toFixed(1)
                        : '',
                }),
            );
            return;
        }

        // If the rejection is not due to too many files or file size, it must be due to an unsupported file type
        snackbarService.showError(
            t('common.dropzone.fileTypeNotSupported', {
                fileName: rejection.file.name,
            }),
        );
    };

    const { getRootProps, getInputProps, isDragActive, isDragReject } =
        useDropzone({
            accept,
            maxSize: maxFileSize,
            multiple: false,
            onDropAccepted: ([file]) => onDropAccepted(file),
            onDropRejected: handleDropRejected,
        });

    return (
        <StyDropzoneBox
            {...getRootProps()}
            isActive={isDragActive}
            isInvalid={isDragReject}
        >
            <input {...getInputProps()} />
            {previewSrc ? (
                <StyPreviewImageContainer>
                    <StyPreviewImage src={previewSrc} alt="preview" />
                    {onRemove && (
                        <StyRemoveButton
                            size="small"
                            aria-label="remove"
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove();
                            }}
                        >
                            <DeleteIcon />
                        </StyRemoveButton>
                    )}
                </StyPreviewImageContainer>
            ) : (
                <>
                    <Typography variant="h5" sx={{ my: 3 }}>
                        {dropzoneText}
                    </Typography>
                    <CloudUploadIcon fontSize="large" />
                </>
            )}
        </StyDropzoneBox>
    );
};

export default DropzoneZone;
